import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { useSelector } from "react-redux";
import { Button } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useTQuery } from "../../../utils/useTQuery";

// PDF.js worker configuration removed - let react-pdf handle it automatically

// Fixed PDF dimensions for coordinate consistency
const PDF_WIDTH = 800;
const PDF_HEIGHT = 600;

const PdfEditorClinician = ({
  pdfFile,
  pdfName,
  onSave,
  onClose,
  isReadOnly = false,
  initialFields = [],
  onFieldsChange = null,
  showPreviewOnly = false,
  documentId = null,
  userId = null,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [modifiedPdfUrl, setModifiedPdfUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfFileObject, setPdfFileObject] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const [savedFieldValues, setSavedFieldValues] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [missingRequiredFieldIds, setMissingRequiredFieldIds] = useState([]);
  const [showRequiredModal, setShowRequiredModal] = useState(false);
  const [missingRequiredLabels, setMissingRequiredLabels] = useState([]);

  const pdfContainerRef = useRef(null);
  const fieldInputRefs = useRef({}); // Map to store refs for all field inputs
  
  // Focus the input field when selectedField changes
  useEffect(() => {
    if (selectedField && fieldInputRefs.current[selectedField]) {
      const inputElement = fieldInputRefs.current[selectedField];
      setTimeout(() => {
        inputElement.focus();
        // For text inputs, select all text so user can start typing immediately
        if (inputElement.type === 'text' || inputElement.type === 'email' || inputElement.type === 'tel' || inputElement.type === 'date' || inputElement.type === 'datetime-local') {
          inputElement.select?.();
        }
      }, 0);
    }
  }, [selectedField]);
  
  // Get user profile data from Redux store and API
  const reduxState = useSelector((state) => state.profileTabSection || {});
  const { selectedTabData } = reduxState;
  const reduxUserProfile = selectedTabData?.about || {};
  const [apiUserProfile, setApiUserProfile] = useState({});

  // Fetch user details from API as fallback
  const { data: userDetails } = useTQuery(
    ['userDetails', userId],
    userId ? `fetchUserDetailsByUserID?${new URLSearchParams({ userId }).toString()}` : null,
    'get'
  );

  useEffect(() => {
    if (userDetails?.OUTPUT) {
      setApiUserProfile(userDetails.OUTPUT);
    }
  }, [userDetails]);

  // Use Redux profile if available, otherwise use API profile
  const userProfile = Object.keys(reduxUserProfile).length > 0 ? reduxUserProfile : apiUserProfile;

  // Use fixed PDF dimensions for consistent coordinate handling
  const getRenderedPageWidth = () => PDF_WIDTH;
  const getRenderedPageHeight = () => PDF_HEIGHT;

  // Convert URL to File object if needed
  useEffect(() => {
    const loadPdfFile = async () => {
      if (!pdfFile) return;

      if (pdfFile instanceof File) {
        console.log('PDF File object received:', pdfFile.name, pdfFile.size, pdfFile.type);
        
        if (pdfFile.type !== 'application/pdf') {
          console.error('Invalid file type:', pdfFile.type);
          setPdfLoadError(new Error('Invalid file type. Please upload a PDF file.'));
          setIsLoadingPdf(false);
          return;
        }

        if (pdfFile.size > 10 * 1024 * 1024) {
          console.error('File too large:', pdfFile.size);
          setPdfLoadError(new Error('File too large. Please upload a PDF smaller than 10MB.'));
          setIsLoadingPdf(false);
          return;
        }

        setPdfFileObject(pdfFile);
        setIsLoadingPdf(false);
        return;
      }

      if (typeof pdfFile === 'string') {
        setIsLoadingPdf(true);
        try {
          const response = await fetch(pdfFile);
          if (!response?.ok) {
            throw new Error(`Failed to fetch PDF: ${response?.statusText}`);
          }
          const blob = await response?.blob();
          const fileName = pdfName || pdfFile.split('/').pop() || 'document.pdf';
          const file = new File([blob], fileName, { type: 'application/pdf' });
          setPdfFileObject(file);
        } catch (error) {
          console.error('Error loading PDF from URL:', error);
          alert('Failed to load PDF. Please check the URL and try again.');
        } finally {
          setIsLoadingPdf(false);
        }
      }
    };

    loadPdfFile();
  }, [pdfFile, pdfName]);

  // Auto-fill fields with user profile data
  const autoFillUserData = () => {
    const updatedFields = fields.map(field => {
      const rawLabel = field.label || '';
      const label = rawLabel.toLowerCase();
      const type = field.type;

      // First name mapping: prefer explicit FirstName type, then label match
      if (type === 'FirstName' || label.includes('first name')) {
        return { ...field, value: userProfile.FIRST_NAME || '' };
      }

      // Last name mapping: prefer explicit LastName type, then label match
      if (type === 'LastName' || label.includes('last name')) {
        return { ...field, value: userProfile.LAST_NAME || '' };
      }

      // Generic Name / Full Name fields keep existing behavior (full name)
      if (
        label.includes('full name') ||
        label === 'name' ||
        label.includes(' name')
      ) {
        const fullName =
          userProfile.FULL_NAME ||
          `${userProfile.FIRST_NAME || ''} ${userProfile.LAST_NAME || ''}`.trim();
        return { ...field, value: fullName };
      }

      // Check if field is for phone number
      if (
        label.includes('phone') ||
        label.includes('mobile') ||
        label.includes('contact')
      ) {
        return { ...field, value: userProfile.PHONE_NUMBER || '' };
      }

      // Check if field is for email
      if (label.includes('email')) {
        // Use EMAIL_ADDRESS instead of EMAIL
        return { ...field, value: userProfile.EMAIL_ADDRESS || '' };
      }

      return field;
    });

    setFields(updatedFields);
  };

  // Keep fields state in sync when initialFields change and handle PDF_FIELD_VALUES
  useEffect(() => {
    
    if (Array.isArray(initialFields) && initialFields.length > 0) {
      let fieldsToSet = [...initialFields];
      const initialSavedValues = {};
      
      // Check if we have PDF_FIELD_VALUES in the response
      if (initialFields.PDF_FIELD_VALUES) {
        const fieldValues = initialFields.PDF_FIELD_VALUES;
        
        // Handle all possible formats of PDF_FIELD_VALUES:
        // 1. Direct field-value mapping: { "fieldId1": "value1" }
        // 2. Object with fields array: { fields: [{ id: "field1", value: "value1" }] }
        // 3. Object with fieldValues: { fieldValues: { "field1": "value1" } }
        
        let valuesToUse = {};
        
        // Case 1: Direct field-value mapping
        if (fieldValues.new_1762924814179_pslf0gn4p || fieldValues.new_1762924817936_zngkevq3o) {
          valuesToUse = fieldValues;
        }
        // Case 2: Has fields array
        else if (Array.isArray(fieldValues.fields)) {
          fieldValues.fields.forEach(field => {
            if (field.id && field.value !== undefined) {
              valuesToUse[field.id] = field.value;
            }
          });
        }
        // Case 3: Has fieldValues object
        else if (fieldValues.fieldValues) {
          valuesToUse = fieldValues.fieldValues;
        }
        
        // Update fields with values from valuesToUse
        fieldsToSet = fieldsToSet.map(field => {
          if (field.id && valuesToUse[field.id] !== undefined) {
            console.log(`Prefilling field ${field.id} with value:`, valuesToUse[field.id]);
            return {
              ...field,
              value: valuesToUse[field.id] || ''
            };
          }
          return field;
        });
      }
      
      // Initialize saved field values from the updated fields
      fieldsToSet.forEach(field => {
        if (field.value && field.value.trim()) {
          initialSavedValues[field.id] = field.value;
        }
      });
      
      setFields(fieldsToSet);
      setSavedFieldValues(initialSavedValues);
      
    } else {
      console.log('PdfEditor - No fields to load or empty array');
    }
  }, [initialFields?.length, JSON.stringify(initialFields)]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    
    // Use only provided initialFields for clinicians
    const makeKey = (f) => f?.id || `${f?.page}-${f?.x}-${f?.y}-${f?.type}-${f?.width}-${f?.height}`;
    const unique = Array.isArray(initialFields)
      ? Array.from(new Map(initialFields.map(f => [makeKey(f), f])).values())
      : [];
    setFields(unique);
  };

  // Generate PDF with form fields
  const generatePdfWithFormFields = async () => {
    if (!pdfFileObject || !fields.length) {
      console.log('No PDF file or fields to process');
      return null;
    }

    try {
      const arrayBuffer = await pdfFileObject.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const form = pdfDoc.getForm();
      
      const existingFields = form.getFields();
      
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      
      if (existingFields.length > 0) {
        // PDF has form fields - fill them
        
        const fieldValueMap = {};
        fields.forEach(field => {
          if (field.value && field.value.trim()) {
            fieldValueMap[field.id] = field.value;
            if (field.originalFieldName) fieldValueMap[field.originalFieldName] = field.value;
            if (field.label) fieldValueMap[field.label] = field.value;
          }
        });
        
        let fieldsFilledCount = 0;
        existingFields.forEach((pdfField) => {
          try {
            const fieldName = pdfField.getName();
            const fieldType = pdfField.constructor.name;
            
            let valueToSet = null;
            if (fieldValueMap[fieldName]) {
              valueToSet = fieldValueMap[fieldName];
            } else {
              for (const [key, value] of Object.entries(fieldValueMap)) {
                if (key && fieldName && (
                  fieldName.toLowerCase().includes(key.toLowerCase()) || 
                  key.toLowerCase().includes(fieldName.toLowerCase())
                )) {
                  valueToSet = value;
                  break;
                }
              }
            }
            
            if (valueToSet) {
              if (fieldType === 'PDFTextField') {
                pdfField.setText(valueToSet);
              } else if (fieldType === 'PDFCheckBox') {
                const shouldCheck = valueToSet === 'true' || valueToSet === true || valueToSet === '1';
                if (shouldCheck) pdfField.check(); else pdfField.uncheck();
              } else if (fieldType === 'PDFDropdown') {
                pdfField.select(valueToSet);
              } else if (fieldType === 'PDFRadioGroup') {
                pdfField.select(valueToSet);
              }
              fieldsFilledCount++;
            }
          } catch (error) {
            console.error('Error filling field:', pdfField.getName(), error);
          }
        });
        
        console.log(`Successfully filled ${fieldsFilledCount} out of ${existingFields.length} PDF fields`);
        if (fieldsFilledCount > 0) {
          form.flatten();
        }
      } else {
        // PDF has no form fields - create form fields for preview
        console.log('PDF has no form fields, creating form fields for preview...');
        
        let fieldsCreatedCount = 0;
        fields.forEach((field) => {
          if (field.value && field.value.trim()) {
            try {
              const pageIndex = (field.page || 1) - 1;
              const page = pdfDoc.getPage(pageIndex);
              const { width: pWidth, height: pHeight } = page.getSize();
              
              // Calculate coordinates to match exactly where the editable fields appear
              const scaleFactor = pWidth / getRenderedPageWidth();
              const x = field.x * scaleFactor;
              const y = pHeight - (field.y * scaleFactor) - (field.height * scaleFactor);
              const width = field.width * scaleFactor;
              const height = field.height * scaleFactor;
              
              // For radio buttons, draw a circle with a dot if selected
              if (field.type === 'Radio') {
                // Draw outer circle
                page.drawCircle({
                  x: x + width / 2,
                  y: y + height / 2,
                  size: Math.min(width, height) * 0.4,
                  borderColor: rgb(0.2, 0.4, 0.8),
                  borderWidth: 1.5,
                });
                
                // Draw inner dot if selected
                if (field.value) {
                  page.drawCircle({
                    x: x + width / 2,
                    y: y + height / 2,
                    size: Math.min(width, height) * 0.2,
                    color: rgb(0.2, 0.4, 0.8),
                  });
                }
              } 
              // For checkboxes, draw a square with a tick if checked
              else if (field.type === 'Checkbox') {
                // Draw outer square
                const size = Math.min(width, height) * 0.8;
                const xPos = x + (width - size) / 2;
                const yPos = y + (height - size) / 2;
                
                // Standard black border box
                page.drawRectangle({
                  x: xPos,
                  y: yPos,
                  width: size,
                  height: size,
                  borderColor: rgb(0.2, 0.4, 0.8),
                  borderWidth: 2,
                });
                
                // Draw tick if checked
                if (field.value) {
                  // Proper checkmark: bottom-left to upper-right
                  const tickX1 = xPos + (size * 0.32);
                  const tickY1 = yPos + (size * 0.45);
                  const tickX2 = xPos + (size * 0.42);
                  const tickY2 = yPos + (size * 0.28);
                  const tickX3 = xPos + (size * 0.78);
                  const tickY3 = yPos + (size * 0.68);

                  // Draw the tick mark in black with a slightly thicker stroke
                  page.drawLine({
                    start: { x: tickX1, y: tickY1 },
                    end: { x: tickX2, y: tickY2 },
                    thickness: 1.5,
                    color: rgb(0.2, 0.4, 0.8),
                  });
                  
                  page.drawLine({
                    start: { x: tickX2, y: tickY2 },
                    end: { x: tickX3, y: tickY3 },
                    thickness: 1.5,
                    color: rgb(0.2, 0.4, 0.8),
                  });
                }
              } else {
                // For other field types, draw the text value directly (no colored field box)
                const value = (typeof field.value === 'string' ? field.value : String(field.value || ''));
                if (value) {
                  // Pick a readable size inside the field box
                  const fontSize = Math.max(Math.min(height * 0.7, 14), 8);
                  const textX = x + 2; // small left padding
                  const textY = y + Math.max((height - fontSize) / 2, 1);

                  // Paint a white rectangle then outline it in blue to mimic field styling
                  page.drawRectangle({
                    x,
                    y,
                    width,
                    height,
                    color: rgb(1, 1, 1),
                    borderColor: rgb(1, 1, 1),
                    borderWidth: 0
                  });

                  page.drawRectangle({
                    x,
                    y,
                    width,
                    height,
                    borderColor: rgb(0, 0, 0),
                    borderWidth: 1,
                  });

                  page.drawText(value, {
                    x: textX,
                    y: textY,
                    size: fontSize,
                    font,
                    color: rgb(0, 0, 0)
                  });
                }
              }
              
              console.log(`Created preview field "${field.id}" with value "${field.value}"`);
              fieldsCreatedCount++;
            } catch (error) {
              console.error('Error creating preview field:', field.id, error);
            }
          }
        });
        
        console.log(`Successfully created ${fieldsCreatedCount} preview fields in PDF`);
        
        // Flatten the form to make fields non-editable in the preview PDF
        if (fieldsCreatedCount > 0) {
          form.flatten();
        }
      }

      // Save PDF without any form fields or editable elements
      const pdfBytes = await pdfDoc.save({ 
        useObjectStreams: false, 
        addDefaultPage: false,
        updateFieldAppearances: false // Prevent field appearances from being saved
      });
      return new Blob([pdfBytes], { type: "application/pdf" });
    } catch (error) {
      console.error("Error generating PDF with form fields:", error);
      return null;
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Update preview
  const updatePreview = async () => {
    console.log('updatePreview called - fields.length:', fields.length, 'showPreviewOnly:', showPreviewOnly, 'pdfFileObject:', !!pdfFileObject);
    
    // For showPreviewOnly mode, always generate preview even with no fields
    if (fields.length === 0 && !showPreviewOnly) {
      console.log('No fields and not showPreviewOnly mode - clearing preview');
      if (modifiedPdfUrl) {
        URL.revokeObjectURL(modifiedPdfUrl);
        setModifiedPdfUrl(null);
      }
      return;
    }

    // If no fields but showPreviewOnly, use original PDF
    if (fields.length === 0 && showPreviewOnly && pdfFileObject) {
      console.log('No fields but showPreviewOnly mode - using original PDF');
      if (modifiedPdfUrl) {
        URL.revokeObjectURL(modifiedPdfUrl);
      }
      const newUrl = URL.createObjectURL(pdfFileObject);
      console.log('Created preview URL for original PDF:', newUrl);
      setModifiedPdfUrl(newUrl);
      return;
    }

    console.log('Generating PDF with form fields...');
    const pdfBlob = await generatePdfWithFormFields();
    if (pdfBlob) {
      console.log('PDF blob generated successfully');
      if (modifiedPdfUrl) {
        URL.revokeObjectURL(modifiedPdfUrl);
      }
      const newUrl = URL.createObjectURL(pdfBlob);
      console.log('Created preview URL for filled PDF:', newUrl);
      setModifiedPdfUrl(newUrl);
    } else {
      console.log('Failed to generate PDF blob');
    }
  };

  // Auto-update preview when fields change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      updatePreview();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [fields]);

  // Generate initial preview for showPreviewOnly mode when PDF loads
  useEffect(() => {
    console.log('Preview useEffect triggered - showPreviewOnly:', showPreviewOnly, 'pdfFileObject:', !!pdfFileObject, 'numPages:', numPages);
    if (showPreviewOnly && pdfFileObject && numPages) {
      console.log('Generating initial preview for showPreviewOnly mode');
      // Add a small delay to ensure everything is loaded
      setTimeout(() => {
        updatePreview();
      }, 100);
    }
  }, [showPreviewOnly, pdfFileObject, numPages]);

  // Also trigger preview when fields are loaded in showPreviewOnly mode
  useEffect(() => {
    if (showPreviewOnly && fields.length > 0 && pdfFileObject) {
      console.log('Fields loaded in showPreviewOnly mode, updating preview');
      updatePreview();
    }
  }, [showPreviewOnly, fields.length, pdfFileObject]);

  // Download the filled PDF
  const handleDownloadPdf = async () => {
    const pdfBlob = await generatePdfWithFormFields();
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pdfName || "filled_document"}_completed.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Sync fields with parent component only when fields actually change
  useEffect(() => {
    if (onFieldsChange && fields.length > 0) {
      onFieldsChange(fields);
    }
    // Intentionally omitting onFieldsChange from dependencies to prevent infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const handleFieldClick = (fieldId) => {
    setSelectedField(fieldId);
    // Always allow editing on single click
    setEditingField(fieldId);
  };

  const handleFieldDoubleClick = (fieldId) => {
    // Double click also enters edit mode
    setEditingField(fieldId);
    setSelectedField(fieldId);
  };

  const handleFieldBlur = (fieldId) => {
    // Exit edit mode when field loses focus
    if (editingField === fieldId) {
      setEditingField(null);
    }
    // Don't clear selectedField here - it's needed for keyboard navigation
    // Only clear it when user explicitly presses Escape
  };

  // Navigate to next field
  const navigateToNextField = () => {
    if (!selectedField || fields.length === 0) return;
    const currentIndex = fields.findIndex(f => f.id === selectedField);
    if (currentIndex < fields.length - 1) {
      const nextField = fields[currentIndex + 1];
      setSelectedField(nextField.id);
      setEditingField(nextField.id);
    }
  };

  // Navigate to previous field
  const navigateToPreviousField = () => {
    if (!selectedField || fields.length === 0) return;
    const currentIndex = fields.findIndex(f => f.id === selectedField);
    if (currentIndex > 0) {
      const prevField = fields[currentIndex - 1];
      setSelectedField(prevField.id);
      setEditingField(prevField.id);
    }
  };

  // Handle keyboard navigation for fields
  const handleFieldKeyDown = (fieldId, e) => {
    if (isReadOnly) return;

    // Tab - move to next field
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        navigateToPreviousField();
      } else {
        navigateToNextField();
      }
    }
    // Enter - move to next field
    else if (e.key === 'Enter' && e.ctrlKey === false) {
      // Don't prevent default for textarea-like fields, only for single-line inputs
      const currentField = fields.find(f => f.id === fieldId);
      if (currentField && currentField.type !== 'signature' && currentField.type !== 'Signature Field') {
        e.preventDefault();
        navigateToNextField();
      }
    }
    // Arrow Down - move to next field
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      navigateToNextField();
    }
    // Arrow Up - move to previous field
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      navigateToPreviousField();
    }
    // Escape - exit edit mode
    else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingField(null);
      setSelectedField(null);
    }
  };

  const handleFieldChange = (fieldId, value, shouldSave = false) => {
    console.log(`Field ${fieldId} changed to:`, value, 'shouldSave:', shouldSave);
    
    const updatedFields = fields.map(field => {
      if (field.id === fieldId) {
        return { ...field, value };
      }
      return field;
    });
    
    setFields(updatedFields);
    
    if (shouldSave) {
      setSavedFieldValues(prev => ({ ...prev, [fieldId]: value }));
      setEditingField(null);
    }
    
    // Call parent callback if provided
    if (onFieldsChange) {
      onFieldsChange(updatedFields);
    }
  };

  const handleSave = async () => {
    if (!pdfFileObject || !pdfName?.trim()) {
      alert("Please upload a PDF and provide a name");
      return;
    }

    // Validate required fields before saving
    const missingRequired = fields.filter((field) => {
      if (field.isRequired !== true) return false;
      // Treat different field types appropriately
      if (field.type === 'Checkbox') {
        // For clinician, checkbox value is stored as a single string when selected
        return !field.value || (typeof field.value === 'string' && !field.value.trim());
      }
      // For other types, empty string / falsy value counts as missing
      return !field.value || (typeof field.value === 'string' && !field.value.trim());
    });

    if (missingRequired.length > 0) {
      const ids = missingRequired.map((f) => f.id);
      setMissingRequiredFieldIds(ids);
      const labels = missingRequired
        .map((f) => f.label || f.originalFieldName || 'Field');
      setMissingRequiredLabels(labels);
      setShowRequiredModal(true);
      return;
    }

    // Clear any previous validation highlighting on successful validation
    if (missingRequiredFieldIds.length) {
      setMissingRequiredFieldIds([]);
    }

    setIsSaving(true);
    try {
      const pdfBlob = await generatePdfWithFormFields();
      if (pdfBlob) {
        const newPdfFile = new File([pdfBlob], `${pdfName}_updated.pdf`, {
          type: "application/pdf",
        });

        // Merge current field values with existing saved values
        const currentFieldValues = fields.reduce((acc, field) => {
          if (field.value && field.value.trim()) {
            acc[field.id] = field.value;
          }
          return acc;
        }, {});
        
        // Merge with existing saved values to preserve previously saved fields
        const mergedSavedValues = { ...savedFieldValues, ...currentFieldValues };
        setSavedFieldValues(mergedSavedValues);

        if (onSave) {
          onSave(newPdfFile, {
            fields: fields,
            fieldValues: mergedSavedValues,
            isTemplateUpdate: true
          });
        }

        // Update preview with saved PDF and exit edit mode
        try {
          const objectUrl = URL.createObjectURL(newPdfFile);
          setModifiedPdfUrl(objectUrl);
          setSelectedField(null);
          setEditingField(null); // Exit edit mode after save
          // Don't update pdfFileObject to prevent field duplication
          // setPdfFileObject(newPdfFile);
        } catch (_) {}
      }
    } catch (error) {
      console.error("Error saving PDF:", error);
      alert("Failed to save PDF. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingPdf) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading PDF...</div>
      </div>
    );
  }

  if (pdfLoadError) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ color: 'red' }}>Error loading PDF: {pdfLoadError.message}</div>
      </div>
    );
  }

  // If showPreviewOnly is true, render only the preview panel
  if (showPreviewOnly) {
    return (
      <div style={{ display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' }}>
        {/* Full-width Preview Panel for Admin/Facility */}
        <div className="test-div" style={{ 
          width: '100%', 
          backgroundColor: 'white', 
          display: 'flex',
          flexDirection: 'column',
               flexWrap: 'wrap',
            gap: '15px'
        }}>
          <div style={{ 
            padding: '16px', 
            borderBottom: '1px solid #ddd',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
       
          }}>
            <div>
              <h4 style={{ margin: 0 }}>clinician's filled form</h4>
              <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '12px' }}>
                {pdfName} - Preview Mode
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="contained"
                onClick={handleDownloadPdf}
                disabled={!modifiedPdfUrl || isGeneratingPdf}
                style={{ 
                  backgroundColor: '#2196F3', 
                  color: 'white',
                  fontSize: '12px',
                  padding: '6px 12px',
                  minWidth: 'auto'
                }}
              >
                {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
              </Button>
              {/* <Button
                variant="outlined"
                onClick={onClose}
                startIcon={<ClearIcon />}
              >
                Close
              </Button> */}
            </div>
          </div>
          
          <div style={{ 
            flex: 1, 
            overflow: 'auto', 
            padding: '15px',
            backgroundColor: '#f0f0f0'
          }}>
            {modifiedPdfUrl || pdfFileObject ? (
              <Document
                file={modifiedPdfUrl || pdfFileObject}
                onLoadError={(error) => console.error('Preview load error:', error)}
                onLoadSuccess={({ numPages: previewNumPages }) => {
                  console.log('Preview PDF loaded with', previewNumPages, 'pages');
                }}
              >
                {Array.from(new Array(numPages || 1), (el, index) => (
                  <div key={`preview_page_${index + 1}`} style={{ 
                    marginBottom: '20px',
                    border: '1px solid #ddd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Page
                      pageNumber={index + 1}
                      width={Math.min(getRenderedPageWidth(), 800)}
                      renderAnnotationLayer={true}
                      renderTextLayer={false}
                    />
                  </div>
                ))}
              </Document>
            ) : (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px',
                color: '#666'
              }}>
                Loading preview...
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh' }}>  
      {/* Main PDF Editor */}
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', maxWidth: '900px' , margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          backgroundColor: isReadOnly ? '#f5f5f5' : 'white', 
          padding: '16px', 
          // borderBottom: isReadOnly ? '2px solid #999' : '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          {/* <div>
            <h3 style={{ margin: 0 }}>
              {isReadOnly ? '👁️ View Mode' : 'PDF Editor'} - {pdfName}
            </h3>
            <p style={{ margin: '4px 0 0 0', color: isReadOnly ? '#d32f2f' : '#666', fontSize: '14px', fontWeight: isReadOnly ? 600 : 400 }}>
              {isReadOnly ? '🔒 This document is saved and locked. You can only view it.' : 'Clinician Mode - Fill in the form fields. Fields marked with * are required.'}
            </p>
          </div> */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center',   flexWrap: 'wrap', }}>
            {!isReadOnly && [
              <>
              <Button
                key="auto-fill"
                variant="outlined"
                onClick={autoFillUserData}
                // disabled={!userProfile.FULL_NAME && !userProfile.FIRST_NAME && !userProfile.PHONE_NUMBER}
                style={{ 
                  backgroundColor: '#2196F3', 
                  color: 'white',
                  fontSize: '12px',
                  padding: '6px 12px',
                  minWidth: 'auto',
                  height: '32px'
                }}
              >
                Auto-fill Profile
              </Button>
              <Button
                key="save"
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || isGeneratingPdf}
                style={{ 
                  backgroundColor: '#4CAF50', 
                  color: 'white',
                  fontSize: '12px',
                  padding: '6px 12px',
                  minWidth: 'auto',
                  height: '32px'
                }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              {documentId === null && (
              <Button
                key="download"
                variant="contained"
                onClick={handleDownloadPdf}
                disabled={!modifiedPdfUrl || isGeneratingPdf}
                style={{ 
                  backgroundColor: '#2196F3', 
                  color: 'white',
                  fontSize: '12px',
                  padding: '6px 12px',
                  minWidth: 'auto',
                  height: '32px'
                }}
              >
                {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
              </Button>
              )}
                   {documentId === null && (
                    <Button
                      key="close"
                      onClick={onClose}
                      variant="outlined"
                      style={{
                        minWidth: '32px',
                        width: '102px',
                        height: '32px',
                        padding: 0,
                        minHeight: '32px',
                        backgroundColor: '#f5f5f5',
                        marginLeft: '8px',
                      }}
                    >
                      Close
                      <ClearIcon fontSize="small" style={{ color: '#666' }} />
                    </Button>
                  )}
            </>
           
            ]}
          </div>
        </div>
        {/* PDF Container */}
        <div
          ref={pdfContainerRef}
          style={{ 
            // flex: 1, 
            width: '100%',
            overflow: 'auto', 
            padding: '15px',
            backgroundColor: '#f8f9fa',
            position: 'relative'
          }}
        >
          {pdfFileObject && (
            <div style={{ position: 'relative' }}>
              <Document
                file={pdfFileObject}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(error) => {
                  console.error('PDF load error:', error);
                  setPdfLoadError(error);
                }}
                loading={null}
              >
                {Array.from(new Array(numPages), (el, index) => {
                  const pageNumber = index + 1;
                  const pageWidth = getRenderedPageWidth();
                  const pageHeight = getRenderedPageHeight();
                  
                  return (
                    <div key={`page_${pageNumber}`} style={{ 
                      marginBottom: '20px', 
                      position: 'relative',
                      border: '1px solid #ddd',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <Page
                        pageNumber={pageNumber}
                        width={pageWidth}
                        height={pageHeight}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                      
                      {/* Render form fields for this page */}
                      {console.log(`All fields:`, fields)}
                      {console.log(`Rendering fields for page ${pageNumber}:`, fields.filter(field => field.page === pageNumber))}
                      {fields
                        .filter(field => field.page === pageNumber)
                        .map((field) => {
                          const isFilled = field.value && field.value.trim();
                          const isSaved = savedFieldValues[field.id];
                          const isEditing = editingField === field.id;
                          const fieldIsReadOnly = isReadOnly; // Use the prop passed to component
                          const isRequired = field.isRequired === true;
                          const isMissingRequired = isRequired && (!field.value || (typeof field.value === 'string' && !field.value.trim()));
                          
                          console.log(`Rendering field ${field.id} at (${field.x}, ${field.y}) with value: "${field.value}"`);
                          return (
                          <div
                            key={field.id}
                            onClick={(e) => {
                              if (fieldIsReadOnly) return;
                              // Prevent event propagation for radio buttons to avoid triggering parent click
                              if (field.type === 'Radio') {
                                e.stopPropagation();
                                // Toggle the radio button
                                handleFieldChange(field.id, field.value ? '' : field.options?.[0]?.value || 'yes');
                              }
                              else if (field.type === 'Checkbox') {
                                e.stopPropagation();
                                // Toggle the checkbox button
                                handleFieldChange(field.id, field.value ? '' : field.options?.[0]?.value || 'yes');
                              } else {
                                handleFieldClick(field.id);
                              }
                            }}
                            onDoubleClick={() => !fieldIsReadOnly && handleFieldDoubleClick(field.id)}
                            style={{
                              position: 'absolute',
                              left: `${field.x}px`,
                              top: `${field.y}px`,
                              width: `${field.width}px`,
                              height: `${field.height}px`,
                              border: selectedField === field.id
                                ? '2px solid #2196F3'
                                : isMissingRequired || missingRequiredFieldIds.includes(field.id)
                                ? '2px solid #f44336'
                                : isSaved
                                ? '2px solid #4CAF50'
                                : isFilled
                                ? '1px solid #FF9800'
                                : '1px solid #ccc',
                              borderRadius: field.type === 'Radio' ? '50%' : '0',
                              backgroundColor: (field.type === 'Radio' || field.type === 'Checkbox') && !fieldIsReadOnly ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 1)',
                              cursor: fieldIsReadOnly ? 'default' : (field.type === 'Radio' || field.type === 'Checkbox' ? 'pointer' : 'text'),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: field.type === 'Radio' ? 'center' : 'flex-start',
                              padding: '2px 4px',
                              fontSize: '12px',
                              zIndex: 10,
                              boxShadow: isSaved ? '0 2px 4px rgba(76, 175, 80, 0.3)' : 
                                        isFilled ? '0 2px 4px rgba(255, 152, 0, 0.3)' : 'none',
                              transition: 'all 0.2s ease',
                              ':hover': (field.type === 'Radio' || field.type === 'Checkbox') && !fieldIsReadOnly ? {
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.1)'
                              } : {},
                              ':active': (field.type === 'Radio' || field.type === 'Checkbox') && !fieldIsReadOnly ? {
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                transform: 'scale(0.98)'
                              } : {}
                            }}
                          >
                            {field.type === 'signature' || field.type === 'Signature Field' ? (
                              <div style={{ 
                                width: '100%', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                padding: 0
                              }}>
                                {/* Text input option */}
                                <input
                                  ref={(el) => {
                                    if (el) fieldInputRefs.current[field.id] = el;
                                  }}
                                  type="text"
                                  value={field.value && !field.value.includes('[Signature Drawn]') ? field.value : ''}
                                  placeholder={field.value ? '' : 'Type signature or draw below'}
                                  readOnly={fieldIsReadOnly}
                                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                  onBlur={() => handleFieldBlur(field.id)}
                                  onKeyDown={(e) => handleFieldKeyDown(field.id, e)}
                                  autoFocus={isEditing}
                                  style={{
                                    width: '100%',
                                    height: '50%',
                                    border: 'none',
                                    borderBottom: '1px solid #ddd',
                                    background: 'transparent',
                                    fontSize: '10px',
                                    padding: '2px 4px',
                                    outline: 'none',
                                    fontFamily: 'cursive',
                                    color: isEditing ? '#2196F3' : (isSaved ? '#4CAF50' : (isFilled ? '#FF9800' : '#000'))
                                  }}
                                />
                                {/* Canvas for drawing signature */}
                                <canvas
                                  ref={(canvas) => {
                                    if (canvas && !fieldIsReadOnly) {
                                      const ctx = canvas.getContext('2d');
                                      let isDrawing = false;
                                      
                                      canvas.width = field.width - 8;
                                      canvas.height = (field.height / 2) - 4;
                                      
                                      const startDrawing = (e) => {
                                        isDrawing = true;
                                        const rect = canvas.getBoundingClientRect();
                                        ctx.beginPath();
                                        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
                                      };
                                      
                                      const draw = (e) => {
                                        if (!isDrawing) return;
                                        const rect = canvas.getBoundingClientRect();
                                        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
                                        ctx.stroke();
                                        // Update field value to indicate signature drawn
                                        !fieldIsReadOnly && handleFieldChange(field.id, '[Signature Drawn]');
                                      };
                                      
                                      const stopDrawing = () => {
                                        isDrawing = false;
                                      };
                                      
                                      canvas.addEventListener('mousedown', startDrawing);
                                      canvas.addEventListener('mousemove', draw);
                                      canvas.addEventListener('mouseup', stopDrawing);
                                      canvas.addEventListener('mouseout', stopDrawing);
                                      
                                      // Clear canvas button
                                      canvas.addEventListener('dblclick', () => {
                                        ctx.clearRect(0, 0, canvas.width, canvas.height);
                                        !fieldIsReadOnly && handleFieldChange(field.id, '');
                                      });
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    height: '50%',
                                    border: '1px solid #ddd',
                                    borderTop: 'none',
                                    cursor: 'crosshair',
                                    backgroundColor: '#fafafa'
                                  }}
                                  title='Draw signature here (double-click to clear)'
                                />
                                {isSaved && !isEditing && (
                                  <span style={{ 
                                    position: 'absolute', 
                                    right: '2px', 
                                    top: '2px',
                                    color: '#4CAF50', 
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}>✓</span>
                                )}
                              </div>
                            ) : field.type === 'Dropdown' ? (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', position: 'relative' }}>
                                <select
                                  ref={(el) => {
                                    if (el) fieldInputRefs.current[field.id] = el;
                                  }}
                                  value={field.value || ''}
                                  onChange={(e) => !fieldIsReadOnly && handleFieldChange(field.id, e.target.value)}
                                  onBlur={() => handleFieldBlur(field.id)}
                                  onKeyDown={(e) => handleFieldKeyDown(field.id, e)}
                                  disabled={fieldIsReadOnly}
                                  autoFocus={selectedField === field.id}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    outline: 'none',
                                    backgroundColor: 'transparent',
                                    fontSize: '12px',
                                    padding: '0 4px',
                                    color: isEditing ? '#2196F3' : (isSaved ? '#4CAF50' : (isFilled ? '#FF9800' : '#000')),
                                    fontWeight: isSaved && !isEditing ? 'bold' : 'normal',
                                    cursor: 'default'
                                  }}
                                  title=""
                                >
                                  <option value="" disabled>
                                    {field.label || "Select..."}
                                  </option>
                                  {(field.options || field.picklistOptions || []).map((opt, idx) => (
                                    <option key={idx} value={typeof opt === 'string' ? opt : opt.value || opt.label}>
                                      {typeof opt === 'string' ? opt : opt.label || opt.value}
                                    </option>
                                  ))}
                                </select>
                                {isSaved && !isEditing && (
                                  <span style={{ 
                                    position: 'absolute', 
                                    right: '2px', 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    color: '#4CAF50', 
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}>✓</span>
                                )}
                              </div>
                            ) :  field.type === 'DateTime' ? (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', position: 'relative' }}>
                              <input
                                ref={(el) => {
                                  if (el) fieldInputRefs.current[field.id] = el;
                                }}
                                type={field.includeTime ? 'datetime-local' : 'date'}
                                value={field.value || ''}
                                onChange={(e) => !fieldIsReadOnly && handleFieldChange(field.id, e.target.value)}
                                onBlur={() => handleFieldBlur(field.id)}
                                onKeyDown={(e) => handleFieldKeyDown(field.id, e)}
                                disabled={fieldIsReadOnly}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  border: 'none',
                                  outline: 'none',
                                  backgroundColor: 'transparent',
                                  fontSize: '12px',
                                  padding: '0 4px',
                                  color: isEditing ? '#2196F3' : (isSaved ? '#4CAF50' : (isFilled ? '#FF9800' : '#000')),
                                  fontWeight: isSaved && !isEditing ? 'bold' : 'normal',
                                  cursor: fieldIsReadOnly ? 'default' : 'pointer'
                                }}
                              />
                              {isSaved && !isEditing && (
                                <span style={{ 
                                  position: 'absolute', 
                                  right: '2px', 
                                  top: '50%', 
                                  transform: 'translateY(-50%)',
                                  color: '#4CAF50', 
                                  fontSize: '10px',
                                  fontWeight: 'bold'
                                }}>✓</span>
                              )}
                            </div>
                          ) : 
                           field.type === 'Radio' ? (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                              padding: '4px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              width: '100%',
                              height: '100%',
                              overflow: 'visible',
                              cursor: fieldIsReadOnly ? 'default' : 'pointer'
                            }}>
                              {(field.options || []).map((option, optIdx) => {
                                const optionValue = typeof option === 'string' ? option : option.value || option.label;
                                const optionLabel = typeof option === 'string' ? option : option.label || option.value;
                                const isChecked = field.value === optionValue;
                                
                                return (
                                  <div 
                                    key={optIdx} 
                                    style={{ 
                                      display: 'flex', 
                                      alignItems: 'center',
                                      margin: '2px 0',
                                      cursor: fieldIsReadOnly ? 'default' : 'pointer',
                                      padding: '2px 4px',
                                      width: '100%',
                                      cursor: fieldIsReadOnly ? 'default' : 'pointer'
                                    }}
                                  >
                                    <div style={{
                                      width: '16px',
                                      height: '16px',
                                      minWidth: '16px',
                                      minHeight: '16px',
                                      border: isChecked ? '2px solid #1976d2' : '2px solid #757575',
                                      borderRadius: '50%',
                                      marginRight: '8px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontSize: '12px',
                                      lineHeight: 1,
                                      transition: 'all 0.2s ease',
                                      borderRadius: '50%',
                                      pointerEvents: 'none',
                                      ...(isChecked && {
                                        backgroundColor: '#000',
                                        borderColor: '#000',
                                        boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.2)'
                                      })
                                    }}>
                                      {isChecked && (
                                        <div style={{
                                          width: '8px',
                                          height: '8px',
                                          borderRadius: '50%',
                                          backgroundColor: '#1976d2',
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          transform: 'translate(-50%, -50%)',
                                          transition: 'all 0.2s ease'
                                        }} />
                                      )}
                                    </div>
                                    <span style={{ 
                                      fontSize: '11px',
                                      color: fieldIsReadOnly ? '#666' : '#333',
                                      userSelect: 'none'
                                    }}>
                                      {optionLabel}
                                    </span>
                                  </div>
                                );
                              })}
                                {isSaved && !isEditing && (
                                  <span style={{
                                    position: 'absolute',
                                    right: '2px',
                                    top: '2px',
                                    color: '#4CAF50',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}>✓</span>
                                )}
                              </div>
                          ) 
                         : field.type === 'Checkbox' ? (
                            <div style={{ width: '100%', padding: '4px' }}>
                              {(field.options || []).map((opt, optIdx) => {
                                const optionValue = typeof opt === 'string' ? opt : opt.value || opt.label;
                                const optionLabel = typeof opt === 'string' ? opt : opt.label || opt.value;
                                const isChecked = field.value === optionValue;

                                return (
                                  <div
                                    key={optIdx}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      margin: '4px 0',
                                      cursor: fieldIsReadOnly ? 'not-allowed' : 'pointer',
                                      userSelect: 'none',
                                      padding: '2px 0',
                                      position: 'relative',
                                      width: '100%' 
                                    }}
                                    onClick={() => {
                                      if (fieldIsReadOnly) return;
                                      const newValue = isChecked ? '' : optionValue;
                                      handleFieldChange(field.id, newValue, true);
                                    }}
                                  >
                                    <label style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={fieldIsReadOnly}
                                        style={{
                                          width: '16px',
                                          height: '16px',
                                          margin: 0,
                                          marginRight: '8px',
                                          cursor: fieldIsReadOnly ? 'not-allowed' : 'pointer',
                                          position: 'relative',
                                          zIndex: 1,
                                          opacity: 0.01
                                        }}
                                      />
                                      <div
                                        style={{
                                          position: 'absolute',
                                          left: 0,
                                          width: '16px',
                                          height: '16px',
                                          border: isChecked ? '2px solid #1976d2' : '2px solid #757575',
                                          backgroundColor: 'white',
                                          marginRight: '8px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: 'white',
                                          fontSize: '12px',
                                          lineHeight: 1,
                                          transition: 'all 0.2s',
                                          borderRadius: '2px',
                                          pointerEvents: 'none',
                                          ...(isChecked && {
                                            backgroundColor: '#000',
                                            borderColor: '#000',
                                            boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.2)'
                                          })
                                        }}
                                      >
                                        {isChecked && '✓'}
                                      </div>

                                      {/* LABEL TEXT */}
                                      <span
                                        style={{
                                          fontSize: '12px',
                                          color: fieldIsReadOnly ? '#999' : '#333',
                                          transition: 'color 0.2s',
                                          marginLeft: '24px'
                                        }}
                                      >
                                        {optionLabel}
                                      </span>
                                    </label>
                                  </div>
                                );
                              })}
                              {isSaved && !isEditing && (
                                <span
                                  style={{
                                    position: 'absolute',
                                    right: '2px',
                                    top: '2px',
                                    color: '#4CAF50',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                            </div>
                          
                          ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', position: 'relative' }}>
                                <input
                                  ref={(el) => {
                                    if (el) fieldInputRefs.current[field.id] = el;
                                  }}
                                  type={field.type === 'Date & Time' ? (field.includeTime ? 'datetime-local' : 'date') : 'text'}
                                  value={field.value || ''}
                                  onChange={(e) => !fieldIsReadOnly && handleFieldChange(field.id, e.target.value)}
                                  onBlur={() => handleFieldBlur(field.id)}
                                  onKeyDown={(e) => handleFieldKeyDown(field.id, e)}
                                  placeholder={field.label}
                                  disabled={fieldIsReadOnly}
                                  autoFocus={selectedField === field.id}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    outline: 'none',
                                    backgroundColor: 'transparent',
                                    fontSize: '12px',
                                    padding: '0',
                                    color: isEditing ? '#2196F3' : (isSaved ? '#4CAF50' : (isFilled ? '#FF9800' : '#000')),
                                    fontWeight: isSaved && !isEditing ? 'bold' : 'normal',
                                    cursor: field.type === 'Date & Time' && !fieldIsReadOnly ? 'pointer' : 'text'
                                  }}
                                />
                                {isSaved && !isEditing && (
                                  <span style={{ 
                                    position: 'absolute', 
                                    right: '2px', 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    color: '#4CAF50', 
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}>✓</span>
                                )}
                              </div>
                            )
                            }
                          </div>
                        );
                        })}
                    </div>
                  );
                })}
              </Document>
            </div>
          )}
        </div>
        
        {/* Read-only Lock Overlay */}
        {/* {isReadOnly && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5
          }}>
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '20px 30px',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              pointerEvents: 'none'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔒</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#d32f2f', marginBottom: '4px' }}>
                Document Locked
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                This document has been saved and is now read-only
              </div>
            </div>
          </div>
        )} */}
      </div>

      {!isReadOnly && showRequiredModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.25)',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 8,
              padding: 16,
              maxWidth: 420,
              width: '90%',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              fontSize: 14,
            }}
          >
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Please fill all required fields</div>
            <div style={{ marginBottom: 12, color: '#4b5563', fontSize: 13 }}>
              The following fields are required before you can save this form:
            </div>
            {missingRequiredLabels.length > 0 && (
              <ul style={{ margin: '0 0 12px 18px', padding: 0, fontSize: 13, color: '#111827' }}>
                {missingRequiredLabels.map((label, idx) => (
                  <li key={idx}>{label}</li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRequiredModal(false)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 4,
                  border: 'none',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfEditorClinician;